import {
  forwardRef,
  ForwardRefExoticComponent,
  InputHTMLAttributes,
  Ref,
  RefAttributes,
} from 'react';
import styled, { css } from 'styled-components';
import isEqual from 'lodash/isEqual';

import { variableFontRegular } from 'styles/theme';

interface IInputStatic {
  Content: any;
  Error: any;
  Input: any;
  Label: any;
  Wrapper: any;
}
interface IAllInputProps extends InputHTMLAttributes<HTMLInputElement> {
  'data-qa': string;
  className?: string;
  errorMessage?: string | boolean;
  hasError?: boolean;
  hasFloatingLabel?: boolean;
  id?: string;
  inputmode?: string;
  label?: string;
  name: string;
}

type InputComponent = ForwardRefExoticComponent<IInputProps & RefAttributes<HTMLInputElement>> &
  IInputStatic;

export type IInputProps = Omit<IAllInputProps, 'placeholder'>;

const ensureEmptyStringIfNil = (param) => {
  if (isEqual(param, 0)) {
    return '0';
  }

  return param || '';
};

export const Input = forwardRef(
  (
    {
      errorMessage,
      className,
      hasError,
      hasFloatingLabel = true,
      label,
      name,
      value,
      ...props
    }: IInputProps,
    ref?: Ref<HTMLInputElement>,
  ) => {
    return (
      <Input.Content className={className} $hasError={hasError}>
        <Input.Wrapper>
          <Input.Input
            aria-labelledby={name}
            name={name}
            placeholder={hasFloatingLabel ? '' : label}
            ref={ref}
            value={ensureEmptyStringIfNil(value)}
            {...props}
          />
          <Input.Label hasFloatingLabel={hasFloatingLabel} hasValue={!!value} id={name}>
            {hasFloatingLabel ? label : name}
          </Input.Label>
        </Input.Wrapper>
        {hasError && (
          <Input.Error data-qa={`${props['data-qa']}_error`}>{errorMessage}</Input.Error>
        )}
      </Input.Content>
    );
  },
) as InputComponent;

Input.Content = styled.div<{ $hasError: boolean }>`
  width: 100%;
  margin-bottom: ${({ $hasError }) => ($hasError ? '32px' : '24px')};
`;

Input.Wrapper = styled.div`
  position: relative;
`;

Input.Input = styled.input`
  display: block;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black};
  border-radius: 0;
  padding: 4px 0 8px;
  width: 100%;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  &:focus {
    outline: none;
    border-bottom-width: 2px;
    /* this ensures the height does not change when we increase border-bottom */
    padding-bottom: 7px;
  }
`;

const activeStyle = css`
  color: inherit;
  transform: translateY(-18px);
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
`;

// Input.Label represents the input placeholder. The placeholder animates/moves above
// the input when the input is in focus or contains a value
Input.Label = styled.label<{ hasFloatingLabel: boolean; hasValue: boolean }>`
  position: absolute;
  pointer-events: none;
  transition: 0.2s ease all;
  left: 0;
  bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  visibility: ${({ hasFloatingLabel }) => (hasFloatingLabel ? 'visible' : 'hidden')};

  ${({ hasValue }) => hasValue && activeStyle};

  ${Input.Input}:focus ~ & {
    ${activeStyle};
  }
`;

Input.Error = styled.div`
  color: ${({ theme }) => theme.colors.error};
  ${variableFontRegular({ size: '9.5px', height: '9px' })};
  margin-top: 8px;
`;
